import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { SEO } from '../components/shared/SEO';
import { 
    ArrowLeft,
    ArrowRight,
    Calendar,
    ChevronRight,
    Clock,
    CloudSun,
    Cpu,
    Database,
    Flame,
    Sparkles,
    Tag,
    TrendingUp,
    User,
    Zap,
    MessageCircle,
    UserPlus,
    FolderOpen,
    Rss,
    Github,
    Mail,
    ExternalLink,
    Clock3,
    Menu,
    X,
    Home as HomeIcon,
    FileText,
    Star,
    Info,
} from 'lucide-react';

interface Moment {
    id: string;
    content: string;
    createdAt: string;
    location?: string;
    images?: string[];
}

interface Post {
    slug: string;
    title: string;
    excerpt: string;
    category: string;
    categoryName?: string;
    tags: string[];
    author: string;
    date: string;
    featured: boolean;
    readingTime: number;
    views?: number;
    comments?: number;
    coverImage?: string;
}

interface Profile {
    name: string;
    title: string;
    bio: string;
    avatar?: string;
    location?: string;
    email?: string;
    website?: string;
    social?: {
        github?: string;
        linkedin?: string;
        twitter?: string;
        zhihu?: string;
    };
    skills: Array<{name: string; level: number}>;
}

const WEEKDAY_LABELS = ['一', '二', '三', '四', '五', '六', '日'];

function getTimestamp(dateString: string) {
    const timestamp = new Date(dateString).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
}

function toDateKey(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function parseDateKey(dateKey: string) {
    const [year, month, day] = dateKey.split('-').map((item) => Number.parseInt(item, 10));
    return new Date(year, month - 1, day);
}

function getCalendarDays(monthDate: Date) {
    const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const startDate = new Date(firstDay);
    startDate.setDate(firstDay.getDate() - startOffset);

    return Array.from({ length: 42 }, (_, index) => {
        const day = new Date(startDate);
        day.setDate(startDate.getDate() + index);
        return day;
    });
}

const Home: React.FC = () => {
    const [posts, setPosts] = useState<Post[]>([]);
    const [profile, setProfile] = useState<Profile | null>(null);
    const [loading, setLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [calendarMonth, setCalendarMonth] = useState(() => {
        const today = new Date();
        return new Date(today.getFullYear(), today.getMonth(), 1);
    });
    const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);
    const [currentTime, setCurrentTime] = useState(() => new Date());
    const [weather, setWeather] = useState<{ location: string; temp: string; condition: string; feelsLike: string; wind: string; humidity: string } | null>(null);
    const [moments, setMoments] = useState<Moment[]>([]);

    const location = useLocation();

    const navItems = [
        { path: '/', label: '首页', icon: HomeIcon },
        { path: '/blog', label: '博客', icon: FileText },
        { path: '/moments', label: '片刻', icon: Star },
        { path: '/about', label: '关于', icon: Info },
    ];

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        const timer = window.setInterval(() => {
            setCurrentTime(new Date());
        }, 60_000);

        return () => window.clearInterval(timer);
    }, []);

    const fetchWeather = async () => {
        try {
            const weatherRes = await fetch('https://wttr.in/Hefei,%20Anhui?format=j1');
            const weatherData = await weatherRes.json();
            const current = weatherData.current_condition?.[0];
            if (current) {
                setWeather({
                    location: weatherData.nearest_area?.[0]?.region?.[0]?.value + ' ' + (weatherData.nearest_area?.[0]?.areaName?.[0]?.value || '') || '合肥市',
                    temp: `${current.temp_C}°C`,
                    condition: current.weatherDesc?.[0]?.value || '未知',
                    feelsLike: `${current.FeelsLikeC}°C`,
                    wind: `${current.windspeedKmph}km/h`,
                    humidity: `${current.humidity}%`,
                });
            }
        } catch (weatherError) {
            console.error('获取天气失败:', weatherError);
        }
    };

    useEffect(() => {
        fetchWeather();
        const weatherTimer = window.setInterval(fetchWeather, 30 * 60 * 1000);
        return () => window.clearInterval(weatherTimer);
    }, []);

    const fetchData = async () => {
        try {
            const [postsRes, profileRes, momentsRes] = await Promise.all([
                fetch('/api/blog-admin/posts'),
                fetch('/api/blog-admin/profile'),
                fetch('/api/blog-admin/moments')
            ]);
            
            const postsData = await postsRes.json();
            const profileData = await profileRes.json();
            
            if (postsData.success) {
                setPosts(postsData.data || []);
            }
            if (profileData.success) {
                setProfile(profileData.data);
            }
            
            if (momentsRes.ok) {
                const momentsData = await momentsRes.json();
                setMoments(Array.isArray(momentsData) ? momentsData.slice(0, 2) : []);
            }
        } catch (error) {
            console.error('获取数据失败:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedDateKey || !posts.length) {
            return;
        }

        const sortedByDate = [...posts].sort((left, right) => getTimestamp(right.date) - getTimestamp(left.date));
        const todayKey = toDateKey(new Date());
        const nextSelectedDateKey = sortedByDate.some((post) => toDateKey(new Date(post.date)) === todayKey)
            ? todayKey
            : toDateKey(new Date(sortedByDate[0].date));

        const selectedDate = parseDateKey(nextSelectedDateKey);
        setSelectedDateKey(nextSelectedDateKey);
        setCalendarMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
    }, [posts, selectedDateKey]);

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('zh-CN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatCompactDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('zh-CN', {
            month: 'numeric',
            day: 'numeric',
        });
    };

    const sortedPosts = [...posts].sort((left, right) => getTimestamp(right.date) - getTimestamp(left.date));
    const featuredPosts = sortedPosts.filter((post) => post.featured).slice(0, 4);
    const latestPosts = sortedPosts.slice(0, 4);
    const hotPosts = [...sortedPosts]
        .sort((left, right) => (right.views || 0) - (left.views || 0))
        .slice(0, 3);

    const totalViews = sortedPosts.reduce((sum, post) => sum + (post.views || 0), 0);
    const totalCategories = new Set(sortedPosts.map((post) => post.category).filter(Boolean)).size;
    const totalTags = new Set(sortedPosts.flatMap((post) => post.tags || []).filter(Boolean)).size;

    const postsByDate = sortedPosts.reduce<Record<string, Post[]>>((accumulator, post) => {
        const dateKey = toDateKey(new Date(post.date));
        if (!accumulator[dateKey]) {
            accumulator[dateKey] = [];
        }

        accumulator[dateKey].push(post);
        return accumulator;
    }, {});

    const categoryColors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
    const topCategories = Object.values(sortedPosts.reduce<Record<string, { key: string; label: string; count: number; color: string }>>((accumulator, post) => {
        const key = post.category || 'uncategorized';
        if (!accumulator[key]) {
            accumulator[key] = {
                key,
                label: post.categoryName || post.category || '未分类',
                count: 0,
                color: categoryColors[Object.keys(accumulator).length % categoryColors.length],
            };
        }

        accumulator[key].count += 1;
        return accumulator;
    }, {})).sort((left, right) => right.count - left.count).slice(0, 6);

    const topTags = Object.entries(sortedPosts.reduce<Record<string, number>>((accumulator, post) => {
        for (const tag of post.tags || []) {
            if (!tag) {
                continue;
            }

            accumulator[tag] = (accumulator[tag] || 0) + 1;
        }

        return accumulator;
    }, {})).sort((left, right) => right[1] - left[1]).slice(0, 5);

    const calendarDays = getCalendarDays(calendarMonth);
    const todayDateKey = toDateKey(currentTime);
    const selectedDatePosts = selectedDateKey ? postsByDate[selectedDateKey] || [] : [];
    const calendarMonthPosts = sortedPosts.filter((post) => {
        const postDate = new Date(post.date);
        return postDate.getFullYear() === calendarMonth.getFullYear() && postDate.getMonth() === calendarMonth.getMonth();
    });
    const latestPost = sortedPosts[0] || null;
    const leadingCategory = topCategories[0] || null;
    const maxCategoryCount = topCategories[0]?.count || 1;
    const monthLabel = calendarMonth.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
    });
    const currentClockLabel = currentTime.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
    });
    const currentDateLabel = currentTime.toLocaleDateString('zh-CN', {
        month: 'long',
        day: 'numeric',
        weekday: 'long',
    });
    const selectedDateLabel = selectedDateKey
        ? parseDateKey(selectedDateKey).toLocaleDateString('zh-CN', {
            month: 'long',
            day: 'numeric',
            weekday: 'long',
        })
        : '选择某一天';

    const contentTopics = ['项目记录', '学习笔记', '生活随笔', '阅读摘记', '观察思考', '灵感片段'];
    const panelClass = 'rounded-2xl md:rounded-[30px] border border-white/75 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] backdrop-blur-xl dark:border-slate-800/80';
    const defaultPanelSurfaceClass = 'bg-white/[0.72] dark:bg-slate-900/[0.58]';
    const softCardClass = 'rounded-xl md:rounded-[24px] border border-white/80 bg-white/[0.86] shadow-[0_18px_48px_-36px_rgba(15,23,42,0.22)] backdrop-blur-md dark:border-slate-800/80 dark:bg-slate-950/[0.42]';
    const statCardClass = 'rounded-xl md:rounded-[24px] border border-white/80 bg-white/90 p-3 sm:p-4 md:p-5 shadow-[0_22px_52px_-40px_rgba(15,23,42,0.25)] backdrop-blur-md dark:border-slate-800/80 dark:bg-slate-950/[0.46]';

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-lg">加载中...</div>
            </div>
        );
    }

    return (
        <div className="relative min-h-screen overflow-hidden bg-slate-50 dark:bg-[#0b1220]">
            <SEO 
                title="首页"
                description={profile?.bio || "Lighting的个人网站。作为一名软件工程师，我在这里记录开发项目、生活点滴与阅读思考。"}
            />

            {/* 移动端侧边栏抽屉 */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-50 flex">
                    <div 
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => setSidebarOpen(false)}
                    />
                    <motion.div
                        initial={{ x: -280 }}
                        animate={{ x: 0 }}
                        exit={{ x: -280 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className="relative w-64 h-full bg-white dark:bg-gray-900 shadow-2xl flex flex-col"
                    >
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                                    <User className="w-4 h-4 text-white" />
                                </div>
                                <span className="font-bold text-gray-800 dark:text-white">{profile?.name || 'LIGHTING'}</span>
                            </div>
                            <button
                                onClick={() => setSidebarOpen(false)}
                                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>
                        <nav className="flex-1 p-4 space-y-2">
                            {navItems.map((item) => {
                                const Icon = item.icon;
                                const isActive = location.pathname === item.path;
                                return (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        onClick={() => setSidebarOpen(false)}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                                            isActive 
                                                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' 
                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                        }`}
                                    >
                                        <Icon className="w-5 h-5" />
                                        <span className="font-medium">{item.label}</span>
                                    </Link>
                                );
                            })}
                        </nav>
                        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                            <Link
                                to="/about"
                                onClick={() => setSidebarOpen(false)}
                                className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                            >
                                <UserPlus className="w-4 h-4" />
                                关于我
                            </Link>
                        </div>
                    </motion.div>
                </div>
            )}

            <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[720px]">
                <div className="absolute left-[7%] top-12 h-72 w-72 rounded-full bg-sky-200/[0.65] blur-3xl dark:bg-sky-500/[0.12]" />
                <div className="absolute right-[8%] top-16 h-80 w-80 rounded-full bg-emerald-100/75 blur-3xl dark:bg-emerald-500/[0.12]" />
                <div className="absolute left-[35%] top-40 h-56 w-56 rounded-full bg-amber-100/70 blur-3xl dark:bg-amber-400/10" />
            </div>

            <div className="relative z-10">
                {/* Hero Section */}
                <section className="relative px-2 sm:px-4 pb-4 sm:pb-6 pt-3 sm:pt-6 md:px-6 lg:px-8 xl:pt-20">
                    <div className="w-full max-w-7xl mx-auto">
                        {/* 移动端极简Header */}
                        <div className="flex items-center justify-between gap-3 mb-3 md:hidden">
                            <div className="flex items-center gap-2 min-w-0">
                                <div className="relative shrink-0">
                                    <div className="h-9 w-9 overflow-hidden rounded-full border-2 border-white/75 bg-white/[0.85] shadow-sm">
                                        {profile?.avatar ? (
                                            <img src={profile.avatar} alt={profile.name || '头像'} className="h-full w-full object-cover" />
                                        ) : (
                                            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-500">
                                                <User className="h-4 w-4 text-white" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <h1 className="text-base font-bold text-gray-800 dark:text-white truncate">
                                    {profile?.name || 'LIGHTING'}
                                </h1>
                            </div>
                            <button
                                onClick={() => setSidebarOpen(true)}
                                className="p-2 rounded-lg bg-white/80 dark:bg-gray-800/80 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                            </button>
                        </div>

                        {/* 桌面端完整布局 */}
                        <div className={`hidden md:block grid items-center gap-4 sm:gap-6 md:gap-8 lg:grid-cols-[1fr] xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.9fr)] xl:gap-12 px-3 sm:px-6 py-5 sm:py-8 md:px-8 md:py-10 xl:px-12 min-w-0 ${panelClass} ${defaultPanelSurfaceClass}`}>
                            <motion.div
                                initial={{ opacity: 0, x: -40 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6 }}
                                className="space-y-4 sm:space-y-6 md:space-y-8"
                            >
                                {/* 头像 + 名称 */}
                                <div className="flex items-center gap-3">
                                    <div className="relative shrink-0">
                                        <div className="absolute inset-0 rounded-[20px] sm:rounded-[28px] bg-gradient-to-br from-sky-400/25 to-emerald-400/20 blur-2xl" />
                                        <div className="relative h-12 w-12 sm:h-16 sm:w-16 md:h-24 md:w-24 overflow-hidden rounded-[16px] sm:rounded-[20px] border border-white/75 bg-white/[0.85] p-0.5 shadow-xl dark:border-slate-700/80 dark:bg-slate-900/75">
                                            {profile?.avatar ? (
                                                <img src={profile.avatar} alt={profile.name || '头像'} className="h-full w-full rounded-[14px] sm:rounded-[16px] object-cover" />
                                            ) : (
                                                <div className="flex h-full w-full items-center justify-center rounded-[14px] sm:rounded-[16px] bg-gradient-to-br from-blue-500 to-cyan-500">
                                                    <User className="h-6 w-6 sm:h-10 sm:w-10 text-white" />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl xl:text-6xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-sky-500 to-emerald-500 dark:from-blue-400 dark:via-sky-300 dark:to-emerald-300 truncate">
                                            {profile?.name || 'LIGHTING'}
                                        </h1>
                                        <div className="hidden md:flex flex-wrap gap-1.5 sm:gap-2 mt-1">
                                            <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50/90 px-2.5 py-1 text-xs sm:text-sm font-medium text-sky-700 dark:border-sky-900/60 dark:bg-sky-950/40 dark:text-sky-300">
                                                个人记录与写作空间
                                            </span>
                                            <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/[0.85] px-2.5 py-1 text-xs sm:text-sm font-medium text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-900/75 dark:text-slate-300">
                                                {profile?.title || '软件工程师'}
                                            </span>
                                        </div>
                                        {/* 桌面端：关于我按钮 */}
                                        <Link
                                            to="/about"
                                            className="hidden md:inline-flex items-center gap-1.5 text-sm text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300 transition-colors mt-1"
                                        >
                                            <UserPlus className="w-4 h-4" />
                                            关于我
                                        </Link>
                                    </div>
                                </div>

                                {/* 简介 - 桌面端显示，移动端隐藏 */}
                                <p className="hidden md:block max-w-2xl text-sm sm:text-base leading-6 sm:leading-7 text-slate-600 dark:text-slate-300 md:text-lg">
                                    {profile?.bio || '这里持续记录工作、学习、生活与各种值得写下的内容。'}
                                </p>

                                {/* 个人 slogan - 桌面端显示，移动端隐藏 */}
                                <p className="hidden md:block text-sm text-slate-500 dark:text-slate-400 italic">
                                    记录AI技术实践、软件开发心得与生活碎片
                                </p>

                                {/* 话题标签 - 桌面端显示，移动端隐藏 */}
                                <div className="hidden md:flex flex-wrap gap-1.5 sm:gap-2 sm:gap-3">
                                    {contentTopics.map((topic) => (
                                        <span
                                            key={topic}
                                            className="rounded-full border border-white/80 bg-white/[0.82] px-2.5 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-slate-600 shadow-sm dark:border-slate-700/80 dark:bg-slate-900/70 dark:text-slate-300"
                                        >
                                            {topic}
                                        </span>
                                    ))}
                                </div>


                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, x: 40 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.15 }}
                                className="flex flex-col gap-4 sm:gap-5"
                            >
                                <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4">
                                <div className={statCardClass}>
                                    <Database className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500 mb-2 sm:mb-3" />
                                    <div className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white">
                                        {posts.length}
                                    </div>
                                    <div className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                                        文章数量
                                    </div>
                                </div>
                                <div className={statCardClass}>
                                    <Cpu className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-500 mb-2 sm:mb-3" />
                                    <div className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white">
                                        {totalCategories}
                                    </div>
                                    <div className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                                        分类数量
                                    </div>
                                </div>
                                <div className={statCardClass}>
                                    <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-amber-500 mb-2 sm:mb-3" />
                                    <div className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white">
                                        {totalViews}
                                    </div>
                                    <div className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                                        累计阅读
                                    </div>
                                </div>
                                <div className={statCardClass}>
                                    <Tag className="w-6 h-6 sm:w-8 sm:h-8 text-violet-500 mb-2 sm:mb-3" />
                                    <div className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white">
                                        {totalTags}
                                    </div>
                                    <div className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                                        标签数量
                                    </div>
                                </div>
                                {/* 天气信息 */}
                                {weather && (
                                    <div className={`${statCardClass} flex items-center gap-3`}>
                                        <CloudSun className="w-6 h-6 sm:w-8 sm:h-8 text-sky-500 mb-2 sm:mb-3" />
                                        <div>
                                            <div className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white">
                                                {weather.temp}
                                            </div>
                                            <div className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                                                {weather.condition} · {weather.location}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* 分类导航卡片区 */}
                <section className="px-2 sm:px-4 pb-4 sm:pb-6 md:px-6 lg:px-8 max-w-[100vw]">
                    <div className="w-full max-w-7xl mx-auto">
                        {/* 分类标题 */}
                        <div className="flex items-center gap-2 mb-4">
                            <FolderOpen className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                            <h3 className="text-lg font-semibold text-slate-800 dark:text-white">内容分类</h3>
                        </div>
                        {/* 分类卡片 */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                            {topCategories.map((cat) => (
                                <Link
                                    key={cat.key}
                                    to={`/blog?category=${encodeURIComponent(cat.key)}`}
                                    className={`${softCardClass} p-4 flex flex-col items-center text-center transition-all hover:-translate-y-1 hover:shadow-lg group`}
                                >
                                    <div 
                                        className="w-3 h-3 rounded-full mb-2"
                                        style={{ backgroundColor: cat.color || '#6366f1' }}
                                    />
                                    <span className="font-medium text-slate-800 dark:text-white text-sm group-hover:text-sky-600 dark:group-hover:text-sky-400 transition-colors">
                                        {cat.label}
                                    </span>
                                    <span className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                        {cat.count} 篇
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="px-2 sm:px-4 py-4 sm:py-8 md:px-6 lg:px-8 max-w-[100vw]">
                    <div className={`w-full max-w-7xl mx-auto grid gap-3 sm:gap-6 p-2.5 sm:p-6 md:p-8 lg:grid-cols-1 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,340px)] xl:p-10 min-w-0 ${panelClass} bg-gradient-to-br from-white/[0.82] via-white/[0.72] to-emerald-50/[0.88] dark:from-slate-900/[0.64] dark:via-slate-900/[0.56] dark:to-slate-950/[0.82]`}>
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55, delay: 0.1 }}
                            className="space-y-3 sm:space-y-6 min-w-0"
                        >
                            <div className={`${softCardClass} p-3 sm:p-6 md:p-7 min-w-0`}>
                                <div className="flex flex-col gap-3 sm:gap-4 border-b border-slate-200/70 pb-4 sm:pb-5 dark:border-slate-800/70 md:flex-row md:items-end md:justify-between min-w-0">
                                    <div className="min-w-0">
                                        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50/90 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300">
                                            <Sparkles className="h-3.5 w-3.5" />
                                            精选与记录
                                        </div>
                                        <h2 className="mt-3 sm:mt-4 text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white break-words">
                                            分享技术洞察与生活点滴
                                        </h2>
                                        <p className="mt-2 max-w-2xl text-sm leading-6 sm:leading-7 text-slate-600 dark:text-slate-300 md:text-base break-words">
                                            这里汇集了最新发布的内容与项目记录，伴随日历视图可以帮助您快速定位到想看的日期内容，不再错过任何精彩瞬间。
                                        </p>
                                    </div>

                                    <Link
                                        to="/blog"
                                        className="inline-flex whitespace-nowrap shrink-0 items-center gap-2 self-start rounded-xl border border-slate-200 bg-white/[0.88] px-5 py-3 text-sm font-medium text-slate-700 transition-all hover:-translate-y-0.5 hover:bg-white dark:border-slate-700 dark:bg-slate-950/[0.6] dark:text-slate-200 dark:hover:bg-slate-950"
                                    >
                                        浏览全部文章
                                        <ChevronRight className="h-4 w-4" />
                                    </Link>
                                </div>

                                <div className="mt-4 sm:mt-6 grid gap-3 sm:gap-4 lg:grid-cols-[minmax(0,1.08fr)_minmax(280px,0.92fr)] min-w-0">
                                    <div className="rounded-[20px] sm:rounded-[26px] border border-white/80 bg-gradient-to-br from-sky-500 to-cyan-500 p-4 sm:p-6 text-white shadow-[0_28px_70px_-44px_rgba(14,165,233,0.45)] dark:border-sky-400/20 dark:from-sky-600 dark:to-cyan-700 min-w-0 overflow-hidden flex flex-col pt-5">
                                        <div className="flex items-center justify-between gap-2 sm:gap-3">
                                            <div className="inline-flex shrink-0 items-center gap-1.5 sm:gap-2 rounded-full bg-white/15 px-2.5 py-1 text-[10px] sm:px-3 sm:py-1.5 sm:text-xs font-semibold uppercase tracking-[0.2em] text-white/90">
                                                <Clock className="h-3 sm:h-3.5 w-3 sm:w-3.5" />
                                                最近落笔
                                            </div>
                                            <span className="rounded-full bg-white/15 px-2.5 py-1 text-[10px] sm:px-3 sm:py-1 sm:text-xs font-medium text-white/85 shrink-0 truncate">
                                                {latestPost ? formatDate(latestPost.date) : '暂无内容'}
                                            </span>
                                        </div>

                                        {latestPost ? (
                                            <>
                                                <Link
                                                    to={`/blog/${latestPost.slug}`}
                                                    className="mt-4 sm:mt-5 block text-xl sm:text-2xl font-bold leading-snug sm:leading-tight text-white transition-opacity hover:opacity-90 md:text-[2rem] break-words"
                                                >
                                                    {latestPost.title}
                                                </Link>
                                                <p className="mt-3 sm:mt-4 max-w-2xl text-sm leading-6 sm:leading-7 text-white/78 md:text-base break-words line-clamp-3">
                                                    {latestPost.excerpt}
                                                </p>

                                                <div className="mt-4 sm:mt-6 flex flex-wrap gap-2">
                                                    {(latestPost.tags || []).slice(0, 3).map((tag) => (
                                                        <span
                                                            key={tag}
                                                            className="rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-medium text-white/85"
                                                        >
                                                            #{tag}
                                                        </span>
                                                    ))}
                                                </div>

                                                <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-white/78">
                                                    <span>{latestPost.categoryName || latestPost.category || '未分类'}</span>
                                                    <span>{latestPost.readingTime} 分钟阅读</span>
                                                    <span className="flex items-center gap-1">
                                                        <MessageCircle className="w-3.5 h-3.5" />
                                                        {latestPost.comments || 0}
                                                    </span>
                                                    <span>{latestPost.views || 0} 次阅读</span>
                                                </div>
                                            </>
                                        ) : (
                                            <p className="mt-5 text-sm text-white/80">
                                                暂时还没有文章公开发布。
                                            </p>
                                        )}
                                    </div>

                                    <div className="rounded-[20px] sm:rounded-[26px] border border-white/80 bg-white/[0.88] p-4 sm:p-5 shadow-[0_18px_48px_-38px_rgba(15,23,42,0.24)] dark:border-slate-800/80 dark:bg-slate-950/[0.48] min-w-0 flex flex-col">
                                        <div className="flex items-start justify-between gap-2 sm:gap-4 min-w-0">
                                            <div className="min-w-0">
                                                <div className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-sky-200 bg-sky-50/90 px-2.5 py-1 text-[10px] sm:px-3 sm:py-1.5 sm:text-xs font-semibold uppercase tracking-[0.2em] text-sky-700 dark:border-sky-900/70 dark:bg-sky-950/40 dark:text-sky-300">
                                                    <Calendar className="h-3 sm:h-3.5 w-3 sm:w-3.5" />
                                                    选中日期
                                                </div>
                                                <h3 className="mt-3 sm:mt-4 text-lg sm:text-xl font-bold text-slate-800 dark:text-white break-words">
                                                    {selectedDateLabel}
                                                </h3>
                                            </div>
                                            <span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] sm:px-3 sm:py-1 sm:text-xs font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
                                                {selectedDatePosts.length} 篇文章
                                            </span>
                                        </div>

                                        <div className="mt-4 space-y-3 min-w-0">
                                            {selectedDatePosts.length > 0 ? selectedDatePosts.map((post) => (
                                                <Link
                                                    key={post.slug}
                                                    to={`/blog/${post.slug}`}
                                                    className="block rounded-[20px] border border-slate-200/80 bg-slate-50/70 px-4 py-4 transition-all hover:-translate-y-0.5 hover:border-sky-200 hover:bg-white hover:shadow-md dark:border-slate-800/80 dark:bg-slate-900/50 dark:hover:border-sky-500/40 dark:hover:bg-slate-900/75"
                                                >
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="min-w-0">
                                                            <p className="line-clamp-1 font-semibold text-slate-800 dark:text-white">
                                                                {post.title}
                                                            </p>
                                                            <p className="mt-1 line-clamp-2 text-sm text-slate-500 dark:text-slate-400">
                                                                {post.excerpt}
                                                            </p>
                                                        </div>
                                                        <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 shadow-sm dark:bg-slate-800 dark:text-slate-300">
                                                            {post.categoryName || post.category || '未分类'}
                                                        </span>
                                                    </div>
                                                </Link>
                                            )) : (
                                                <div className="rounded-[20px] border border-dashed border-slate-200 bg-slate-50/70 px-4 py-5 text-sm leading-7 text-slate-500 dark:border-slate-800 dark:bg-slate-950/[0.34] dark:text-slate-400">
                                                    这一天还没有公开文章。你可以在右侧日历切换日期，快速回看其它时间节点。
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={`${softCardClass} p-6 md:p-7`}>
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <h3 className="text-2xl font-bold text-slate-800 dark:text-white">
                                            近期文章
                                        </h3>
                                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                            更靠近首页核心内容的文章列表。
                                        </p>
                                    </div>
                                    <span className="rounded-full border border-emerald-200 bg-emerald-50/90 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300">
                                        本月 {calendarMonthPosts.length} 篇
                                    </span>
                                </div>

                                <div className="mt-5 grid gap-3 md:grid-cols-2">
                                    {latestPosts.map((post) => (
                                        <Link
                                            key={post.slug}
                                            to={`/blog/${post.slug}`}
                                            className="group rounded-[22px] border border-white/80 bg-white/[0.86] p-5 transition-all hover:-translate-y-0.5 hover:border-sky-200 hover:shadow-md dark:border-slate-800/80 dark:bg-slate-950/[0.46] dark:hover:border-sky-500/40 flex flex-col"
                                        >
                                            {post.coverImage && (
                                                <div className="mb-4 -mx-1 -mt-1 overflow-hidden rounded-xl">
                                                    <img src={post.coverImage} alt={post.title} className="w-full h-32 object-cover transition-transform duration-500 group-hover:scale-105" />
                                                </div>
                                            )}
                                            <div className="flex items-center gap-2 text-xs font-medium text-slate-400 dark:text-slate-500">
                                                <span>{formatCompactDate(post.date)}</span>
                                                <span className="h-1 w-1 rounded-full bg-slate-300 dark:bg-slate-600" />
                                                <span>{post.categoryName || post.category || '未分类'}</span>
                                            </div>
                                            <h4 className="mt-3 line-clamp-2 text-lg font-semibold text-slate-800 transition-colors group-hover:text-sky-600 dark:text-white dark:group-hover:text-sky-400">
                                                {post.title}
                                            </h4>
                                            <p className="mt-2 line-clamp-2 text-sm text-slate-500 dark:text-slate-400">
                                                {post.excerpt}
                                            </p>
                                            <div className="mt-4 flex items-center justify-between text-sm text-slate-500 dark:text-slate-400">
                                                <div className="flex items-center gap-3">
                                                    <span>{post.readingTime} 分钟阅读</span>
                                                    <span className="flex items-center gap-1">
                                                        <MessageCircle className="w-3.5 h-3.5" />
                                                        {post.comments || 0}
                                                    </span>
                                                </div>
                                                <span>{post.views || 0} 阅读</span>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </motion.div>

                        <div className="space-y-6">
                            <motion.div
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.55, delay: 0.18 }}
                                className={`${softCardClass} p-5`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50/90 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-sky-700 dark:border-sky-900/70 dark:bg-sky-950/40 dark:text-sky-300">
                                            <Calendar className="h-3.5 w-3.5" />
                                            写作日历
                                        </div>
                                        <p className="mt-4 text-3xl font-black tracking-tight text-slate-900 dark:text-white">
                                            {currentClockLabel}
                                        </p>
                                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                            {currentDateLabel}
                                        </p>
                                    </div>

                                    <div className="rounded-[22px] bg-slate-900 px-4 py-3 text-white shadow-lg dark:bg-slate-100 dark:text-slate-900">
                                        <div className="text-2xl font-bold">
                                            {calendarMonthPosts.length}
                                        </div>
                                        <div className="mt-1 text-[11px] uppercase tracking-[0.24em] opacity-70">
                                            本月更新
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-5 flex items-center justify-between rounded-full border border-white/80 bg-white/90 p-1 shadow-sm dark:border-slate-700/70 dark:bg-slate-950/70">
                                    <button
                                        type="button"
                                        onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
                                        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
                                        aria-label="查看上个月"
                                    >
                                        <ArrowLeft className="h-4 w-4" />
                                    </button>
                                    <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                                        {monthLabel}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
                                        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
                                        aria-label="查看下个月"
                                    >
                                        <ArrowRight className="h-4 w-4" />
                                    </button>
                                </div>

                                <div className="mt-4 grid grid-cols-7 gap-1 sm:gap-2 text-center text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.15em] sm:tracking-[0.2em] text-slate-400 dark:text-slate-500">
                                    {WEEKDAY_LABELS.map((weekday) => (
                                        <div key={weekday} className="py-1">
                                            {weekday}
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-2 grid grid-cols-7 gap-1 sm:gap-2">
                                    {calendarDays.map((day) => {
                                        const dayKey = toDateKey(day);
                                        const dayPosts = postsByDate[dayKey] || [];
                                        const isCurrentMonth = day.getFullYear() === calendarMonth.getFullYear() && day.getMonth() === calendarMonth.getMonth();
                                        const isSelected = dayKey === selectedDateKey;
                                        const isToday = dayKey === todayDateKey;
                                        const hasPosts = dayPosts.length > 0;

                                        return (
                                            <button
                                                key={dayKey}
                                                type="button"
                                                onClick={() => {
                                                    setSelectedDateKey(dayKey);
                                                    if (!isCurrentMonth) {
                                                        setCalendarMonth(new Date(day.getFullYear(), day.getMonth(), 1));
                                                    }
                                                }}
                                                className={`aspect-square rounded-[12px] sm:rounded-[16px] border px-1 py-1.5 sm:px-1.5 sm:py-2 text-center transition-all min-w-[28px] ${
                                                    isSelected
                                                        ? 'border-sky-400 bg-sky-500 text-white shadow-lg shadow-sky-500/20 dark:border-sky-300/60 dark:bg-sky-500/25 dark:text-sky-50'
                                                        : hasPosts
                                                            ? 'border-white/80 bg-white/[0.88] text-slate-700 hover:-translate-y-0.5 hover:border-sky-200 hover:shadow-sm dark:border-slate-800/80 dark:bg-slate-950/[0.48] dark:text-slate-200 dark:hover:border-sky-500/40'
                                                            : 'border-white/70 bg-white/[0.58] text-slate-500 dark:border-slate-800/70 dark:bg-slate-950/[0.28] dark:text-slate-400'
                                                } ${!isCurrentMonth ? 'opacity-45' : ''} ${isToday && !isSelected ? 'ring-2 ring-sky-200 dark:ring-sky-500/40' : ''}`}
                                            >
                                                <div className={`text-xs font-semibold ${isSelected ? 'text-white dark:text-sky-50' : isToday ? 'text-sky-700 dark:text-sky-300' : ''}`}>
                                                    {day.getDate()}
                                                </div>
                                                <div className="mt-2 flex items-center justify-center gap-1">
                                                    {hasPosts ? Array.from({ length: Math.min(dayPosts.length, 3) }, (_, index) => (
                                                        <span
                                                            key={`${dayKey}-${index}`}
                                                            className={`h-1.5 w-1.5 rounded-full ${isSelected ? 'bg-white/75' : 'bg-sky-400 dark:bg-sky-500/70'}`}
                                                        />
                                                    )) : (
                                                        <span className={`h-1.5 w-1.5 rounded-full ${isSelected ? 'bg-white/30' : 'bg-slate-200 dark:bg-slate-700'}`} />
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="mt-5 rounded-[20px] border border-slate-200/80 bg-slate-50/80 p-4 dark:border-slate-800/80 dark:bg-slate-900/50">
                                    <div className="flex items-center justify-between gap-3">
                                        <div>
                                            <p className="text-xs uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
                                                当前选中
                                            </p>
                                            <p className="mt-2 font-semibold text-slate-800 dark:text-white">
                                                {selectedDateLabel}
                                            </p>
                                        </div>
                                        <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm dark:bg-slate-800 dark:text-slate-300">
                                            {selectedDatePosts.length} 篇
                                        </span>
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.55, delay: 0.24 }}
                                className={`${softCardClass} p-5`}
                            >
                                <div className="flex items-center gap-2 text-slate-800 dark:text-white">
                                    <TrendingUp className="h-5 w-5 text-emerald-500" />
                                    <h3 className="text-lg font-bold">
                                        写作重心
                                    </h3>
                                </div>

                                <div className="mt-4 grid grid-cols-2 gap-3">
                                    <div className="rounded-[20px] bg-slate-100/80 p-4 dark:bg-slate-900/70">
                                        <p className="text-xs uppercase tracking-[0.22em] text-slate-400">
                                            主力主题
                                        </p>
                                        <p className="mt-2 text-lg font-semibold text-slate-800 dark:text-white">
                                            {leadingCategory?.label || '待记录'}
                                        </p>
                                    </div>
                                    <div className="rounded-[20px] bg-emerald-50/90 p-4 dark:bg-emerald-950/20">
                                        <p className="text-xs uppercase tracking-[0.22em] text-emerald-600/70 dark:text-emerald-300/70">
                                            主题数量
                                        </p>
                                        <p className="mt-2 text-lg font-semibold text-slate-800 dark:text-white">
                                            {totalCategories} 个分类
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-5 space-y-4">
                                    {topCategories.length > 0 ? topCategories.map((item) => (
                                        <div key={item.key}>
                                            <div className="mb-2 flex items-center justify-between text-sm">
                                                <span className="font-medium text-slate-700 dark:text-slate-200">
                                                    {item.label}
                                                </span>
                                                <span className="text-slate-400 dark:text-slate-500">
                                                    {item.count} 篇
                                                </span>
                                            </div>
                                            <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800">
                                                <div
                                                    className="h-2 rounded-full bg-gradient-to-r from-emerald-400 via-sky-400 to-blue-500"
                                                    style={{ width: `${(item.count / maxCategoryCount) * 100}%` }}
                                                />
                                            </div>
                                        </div>
                                    )) : (
                                        <p className="text-sm text-slate-500 dark:text-slate-400">
                                            暂时还没有可统计的主题数据。
                                        </p>
                                    )}
                                </div>

                                {topTags.length > 0 && (
                                    <div className="mt-5 flex flex-wrap gap-2">
                                        {topTags.map(([tag, count]) => (
                                            <span
                                                key={tag}
                                                className="rounded-full border border-white/80 bg-white/[0.84] px-3 py-1.5 text-xs font-medium text-slate-600 dark:border-slate-700/80 dark:bg-slate-950/[0.46] dark:text-slate-300"
                                            >
                                                #{tag} · {count}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.55, delay: 0.3 }}
                                className={`${softCardClass} p-5`}
                            >
                                <div className="flex items-center gap-2 text-slate-800 dark:text-white">
                                    <Flame className="h-5 w-5 text-rose-500" />
                                    <h3 className="text-lg font-bold">
                                        热读文章
                                    </h3>
                                </div>

                                <div className="mt-4 space-y-3">
                                    {hotPosts.length > 0 ? hotPosts.map((post, index) => (
                                        <Link
                                            key={post.slug}
                                            to={`/blog/${post.slug}`}
                                            className="flex items-start gap-3 rounded-[20px] border border-white/80 bg-white/[0.86] px-4 py-4 transition-all hover:-translate-y-0.5 hover:border-rose-200 hover:shadow-md dark:border-slate-800/80 dark:bg-slate-950/[0.46] dark:hover:border-rose-500/40"
                                        >
                                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-rose-100 text-sm font-bold text-rose-700 dark:bg-rose-950/30 dark:text-rose-300">
                                                {String(index + 1).padStart(2, '0')}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="line-clamp-1 font-semibold text-slate-800 dark:text-white">
                                                    {post.title}
                                                </p>
                                                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                                    {post.views || 0} 次阅读 · {formatCompactDate(post.date)}
                                                </p>
                                            </div>
                                        </Link>
                                    )) : (
                                        <p className="text-sm text-slate-500 dark:text-slate-400">
                                            暂时还没有阅读热度数据。
                                        </p>
                                    )}
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* 精选文章 */}
                {featuredPosts.length > 0 && (
                    <section className="px-3 sm:px-4 py-6 sm:py-8 md:px-6 lg:px-8">
                        <div className={`w-full max-w-7xl mx-auto p-4 sm:p-6 md:p-8 xl:p-10 ${panelClass} ${defaultPanelSurfaceClass}`}>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:mb-8 mb-4">
                                <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white">
                                    精选文章
                                </h2>
                                <Link
                                    to="/blog"
                                    className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 text-sm sm:text-base"
                                >
                                    查看全部
                                    <ChevronRight className="w-4 h-4" />
                                </Link>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                                {featuredPosts.map((post, index) => (
                                    <motion.div
                                        key={post.slug}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.08 }}
                                    >
                                        <Link
                                            to={`/blog/${post.slug}`}
                                            className={`group block h-full p-6 transition-all hover:-translate-y-1 hover:shadow-xl ${softCardClass}`}
                                        >
                                            <div className="flex items-center gap-2 mb-3">
                                                <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-sm">
                                                    {post.categoryName || post.category}
                                                </span>
                                                <span className="text-slate-400 text-sm">
                                                    {post.readingTime}分钟阅读
                                                </span>
                                            </div>
                                            <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                                {post.title}
                                            </h3>
                                            <p className="text-slate-600 dark:text-slate-400 mb-4 line-clamp-2">
                                                {post.excerpt}
                                            </p>
                                            <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                                                <div className="flex items-center gap-1">
                                                    <Calendar className="w-4 h-4" />
                                                    {formatDate(post.date)}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Tag className="w-4 h-4" />
                                                    {post.tags.slice(0, 2).join(', ')}
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* 最新文章 */}
                <section className="px-3 sm:px-4 py-6 sm:py-8 md:px-6 lg:px-8">
                    <div className={`w-full max-w-7xl mx-auto p-4 sm:p-6 md:p-8 xl:p-10 bg-gradient-to-br from-white/[0.78] via-white/[0.68] to-sky-50/[0.85] dark:from-slate-900/[0.62] dark:via-slate-900/[0.56] dark:to-slate-950/[0.78] ${panelClass}`}>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:mb-8 mb-4">
                            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white">
                                最新发布
                            </h2>
                            <Link
                                to="/blog"
                                className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 text-sm sm:text-base"
                            >
                                查看全部
                                <ChevronRight className="w-4 h-4" />
                            </Link>
                        </div>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                            {latestPosts.map((post, index) => (
                                <motion.div
                                    key={post.slug}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 + index * 0.08 }}
                                >
                                    <Link
                                        to={`/blog/${post.slug}`}
                                        className={`group block overflow-hidden p-6 transition-all hover:-translate-y-1 hover:shadow-xl ${softCardClass}`}
                                    >
                                        <div className="flex items-center gap-2 mb-3">
                                            <span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs rounded">
                                                {post.categoryName || post.category}
                                            </span>
                                        </div>
                                        <h3 className="font-bold text-slate-800 dark:text-white mb-2 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                            {post.title}
                                        </h3>
                                        <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                {formatCompactDate(post.date)}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <MessageCircle className="w-3 h-3" />
                                                {post.comments || 0}
                                            </span>
                                        </div>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* 精简介绍 */}
                <section className="px-3 sm:px-4 py-6 sm:py-8 md:px-6 lg:px-8">
                    <div className={`w-full max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10 md:px-8 xl:px-12 bg-gradient-to-r from-white/75 via-white/[0.65] to-emerald-50/[0.85] dark:from-slate-900/60 dark:via-slate-900/[0.55] dark:to-slate-950/[0.76] ${panelClass}`}>
                        <div className="max-w-4xl mx-auto text-center">
                            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white mb-3 sm:mb-4">
                                这里会记录什么
                            </h2>
                            <p className="text-slate-600 dark:text-slate-300 leading-7 sm:leading-8 mb-4 sm:mb-6 text-sm sm:text-base">
                                这里会持续记录工作、学习、生活、阅读和阶段性思考。
                                内容不预设方向，技术、项目、日常感受或任何值得留下的片段，都可以成为一篇文章。
                            </p>
                            <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                                {['工作记录', '学习笔记', '生活片段', '阅读摘记'].map((item) => (
                                    <span
                                        key={item}
                                        className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-white/80 bg-white/[0.82] text-slate-700 dark:border-slate-700/80 dark:bg-slate-900/[0.72] dark:text-slate-300 text-xs sm:text-sm"
                                    >
                                        {item}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* 最新片刻 + 订阅更新 */}
                <section className="px-3 sm:px-4 py-6 sm:py-8 md:px-6 lg:px-8">
                    <div className="w-full max-w-7xl mx-auto grid gap-4 sm:gap-6 lg:grid-cols-2">
                        {/* 最新片刻 */}
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55, delay: 0.1 }}
                            className={`${softCardClass} p-5 sm:p-6`}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <Clock3 className="w-5 h-5 text-amber-500" />
                                    <h3 className="text-lg font-bold text-slate-800 dark:text-white">
                                        最新片刻
                                    </h3>
                                </div>
                                <Link
                                    to="/moments"
                                    className="text-sm text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300 flex items-center gap-1"
                                >
                                    查看全部
                                    <ExternalLink className="w-3.5 h-3.5" />
                                </Link>
                            </div>
                            
                            {moments.length > 0 ? (
                                <div className="space-y-4">
                                    {moments.map((moment) => (
                                        <div
                                            key={moment.id}
                                            className="p-4 rounded-xl bg-slate-50/80 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-700/60"
                                        >
                                            <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2">
                                                {moment.content.length > 80 
                                                    ? moment.content.substring(0, 80) + '...' 
                                                    : moment.content}
                                            </p>
                                            <p className="text-xs text-slate-400 mt-2">
                                                {new Date(moment.createdAt).toLocaleDateString('zh-CN', {
                                                    month: 'numeric',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">
                                    暂无片刻内容
                                </p>
                            )}
                        </motion.div>

                        {/* 订阅更新 */}
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55, delay: 0.18 }}
                            className={`${softCardClass} p-5 sm:p-6`}
                        >
                            <div className="flex items-center gap-2 mb-4">
                                <Rss className="w-5 h-5 text-emerald-500" />
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white">
                                    订阅更新
                                </h3>
                            </div>
                            
                            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
                                获取最新文章、技术笔记与生活碎片
                            </p>
                            
                            <div className="grid grid-cols-3 gap-3">
                                <a
                                    href="/rss.xml"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-orange-50/80 dark:bg-orange-900/20 border border-orange-200/60 dark:border-orange-700/60 hover:-translate-y-0.5 transition-all group"
                                >
                                    <Rss className="w-6 h-6 text-orange-500 group-hover:scale-110 transition-transform" />
                                    <span className="text-xs font-medium text-orange-700 dark:text-orange-300">
                                        RSS订阅
                                    </span>
                                </a>
                                <a
                                    href={`mailto:${profile?.email || 'living20250000@gmail.com'}?subject=订阅更新`}
                                    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-blue-50/80 dark:bg-blue-900/20 border border-blue-200/60 dark:border-blue-700/60 hover:-translate-y-0.5 transition-all group"
                                >
                                    <Mail className="w-6 h-6 text-blue-500 group-hover:scale-110 transition-transform" />
                                    <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                                        邮件订阅
                                    </span>
                                </a>
                                <a
                                    href={profile?.social?.github || 'https://github.com/xxm-666'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-slate-100/80 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-700/60 hover:-translate-y-0.5 transition-all group"
                                >
                                    <Github className="w-6 h-6 text-slate-700 dark:text-slate-300 group-hover:scale-110 transition-transform" />
                                    <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                                        GitHub关注
                                    </span>
                                </a>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* 底部 */}
                <section className="py-8 sm:py-12 px-4 text-center">
                    <p className="text-slate-500 dark:text-slate-400">
                        © 2026 {profile?.name || 'LIGHTING'}. All rights reserved.
                    </p>
                </section>
            </div>
        </div>
    );
};

export default Home;